/*
 * /patient (list page)
 */
import {
	Button,
	Card,
	CardContent,
	Container,
	Input,
	Label,
	Page,
	PageHeader,
	Placeholder,
} from "@emr/react-ui"
import { cn } from "@emr/react-ui"
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router"
import { ChevronDown, ChevronRight, ChevronUp, Plus, User } from "lucide-react"
import { useEffect, useState } from "react"
import { z } from "zod"
import { trpc } from "../../trpc"
import type { ClientStatus, Patient } from "@emr/db/client"

const patientSearchSchema = z.object({
	status: z.string().optional(),
})

export const Route = createFileRoute("/patient/")({
	validateSearch: patientSearchSchema,
	component: PatientListPage,
})

const STATUS_LABELS: Record<string, string> = {
	all: "All Patients",
	none: "No Status",
	review_required: "Review Required",
	education_required: "Education Required",
	education_scheduled: "Education Scheduled",
	prescription_required: "Prescription Required",
	prescription_scheduled: "Prescription Scheduled",
	active: "Active",
	sla_required: "SLA Required",
	sla_scheduled: "SLA Scheduled",
	renewal_required: "Renewal Required",
	not_active: "Not Active",
}

const STATUS_COLORS: Record<ClientStatus, string> = {
	review_required: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
	education_required: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
	education_scheduled: "bg-blue-200 text-blue-800 dark:bg-blue-800/40 dark:text-blue-200",
	prescription_required: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
	prescription_scheduled: "bg-amber-200 text-amber-800 dark:bg-amber-800/40 dark:text-amber-200",
	active: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300",
	sla_required: "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300",
	sla_scheduled: "bg-orange-200 text-orange-800 dark:bg-orange-800/40 dark:text-orange-200",
	renewal_required: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
	not_active: "bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400",
}

/* ─── Debounce hook ──────────────────────────────────────────────────────── */

function useDebounce<T>(value: T, ms = 350): T {
	const [debounced, setDebounced] = useState(value)
	useEffect(() => {
		const t = setTimeout(() => setDebounced(value), ms)
		return () => clearTimeout(t)
	}, [value, ms])
	return debounced
}

/* ─── Search state ───────────────────────────────────────────────────────── */

interface SearchState {
	query: string   // name / client# / K number
	dob: string     // YYYY-MM-DD
	phone: string
	email: string
	address: string
}

const EMPTY_SEARCH: SearchState = { query: "", dob: "", phone: "", email: "", address: "" }

function hasAnySearch(s: SearchState) {
	return !!(s.query || s.dob || s.phone || s.email || s.address)
}

/* ─── Page ───────────────────────────────────────────────────────────────── */

function PatientListPage() {
	const navigate = useNavigate()
	const { status: rawStatus } = Route.useSearch()
	const activeStatus = rawStatus ?? "all"

	const [search, setSearch] = useState<SearchState>(EMPTY_SEARCH)
	const [advancedOpen, setAdvancedOpen] = useState(false)
	const debounced = useDebounce(search)

	const isSearching = hasAnySearch(debounced)

	/* Queue data — loaded when not actively searching */
	const { data: queuePatients, isLoading: queueLoading } = trpc.patient.getAllByClientStatus.useQuery(
		{
			status: (["all", "none", ...Object.keys(STATUS_LABELS)].includes(activeStatus)
				? activeStatus
				: "all") as any,
		},
		{ enabled: !isSearching },
	)

	/* Search data — triggered by any search field */
	const { data: searchResults, isLoading: searchLoading } = trpc.patient.search.useQuery(
		{
			status: activeStatus !== "all" ? (activeStatus as any) : undefined,
			query: debounced.query || undefined,
			dob: debounced.dob || undefined,
			phone: debounced.phone || undefined,
			email: debounced.email || undefined,
			address: debounced.address || undefined,
		},
		{ enabled: isSearching },
	)

	const patients = isSearching ? searchResults : queuePatients
	const isLoading = isSearching ? searchLoading : queueLoading

	const pageTitle = STATUS_LABELS[activeStatus] ?? "Patients"

	function clearSearch() {
		setSearch(EMPTY_SEARCH)
		setAdvancedOpen(false)
	}

	return (
		<Page>
			<Container width="xl" padding="lg" gap="lg">
				<PageHeader
					title={pageTitle}
					right={{
						Component: (
							<Button onClick={() => navigate({ to: "/patient/new" })}>
								<Plus className="w-4 h-4 mr-1" />
								New Patient
							</Button>
						),
					}}
				/>

				{/* Search */}
				<div className="flex flex-col gap-2 max-w-2xl">
					{/* Basic row */}
					<div className="flex gap-2">
						<Input
							placeholder="Search by name, client #, or K number…"
							value={search.query}
							onChange={(e) => setSearch((s) => ({ ...s, query: e.target.value }))}
							className="flex-1"
						/>
						<Input
							type="date"
							title="Date of Birth"
							value={search.dob}
							onChange={(e) => setSearch((s) => ({ ...s, dob: e.target.value }))}
							className="w-40"
						/>
						<Button
							variant="ghost"
							size="sm"
							onClick={() => setAdvancedOpen((o) => !o)}
							className="shrink-0 text-muted-foreground"
						>
							Advanced
							{advancedOpen
								? <ChevronUp className="w-3.5 h-3.5 ml-1" />
								: <ChevronDown className="w-3.5 h-3.5 ml-1" />
							}
						</Button>
						{isSearching && (
							<Button variant="ghost" size="sm" onClick={clearSearch} className="shrink-0">
								Clear
							</Button>
						)}
					</div>

					{/* Advanced fields */}
					{advancedOpen && (
						<div className="grid grid-cols-1 md:grid-cols-3 gap-2 p-3 rounded-lg border bg-muted/30">
							<div className="flex flex-col gap-1">
								<Label className="text-xs text-muted-foreground">Phone</Label>
								<Input
									placeholder="e.g. 902-555-0100"
									value={search.phone}
									onChange={(e) => setSearch((s) => ({ ...s, phone: e.target.value }))}
								/>
							</div>
							<div className="flex flex-col gap-1">
								<Label className="text-xs text-muted-foreground">Email</Label>
								<Input
									placeholder="e.g. john@example.com"
									value={search.email}
									onChange={(e) => setSearch((s) => ({ ...s, email: e.target.value }))}
								/>
							</div>
							<div className="flex flex-col gap-1">
								<Label className="text-xs text-muted-foreground">Address (partial)</Label>
								<Input
									placeholder="e.g. Main St or Halifax"
									value={search.address}
									onChange={(e) => setSearch((s) => ({ ...s, address: e.target.value }))}
								/>
							</div>
						</div>
					)}
				</div>

				{/* Results */}
				{isLoading ? (
					<Placeholder title="Loading…" />
				) : !patients || patients.length === 0 ? (
					<Placeholder
						title={isSearching ? "No patients match your search." : "No patients yet."}
					/>
				) : (
					<div className="flex flex-col gap-3">
						{isSearching && (
							<p className="text-sm text-muted-foreground">
								{patients.length} result{patients.length !== 1 ? "s" : ""}
							</p>
						)}
						{patients.map((patient) => (
							<PatientCard key={patient.id} patient={patient} />
						))}
					</div>
				)}
			</Container>
		</Page>
	)
}

/* ─── Patient card ───────────────────────────────────────────────────────── */

function PatientCard({ patient }: { patient: Patient }) {
	return (
		<Link
			to="/details/$patientId"
			params={{ patientId: patient.id }}
			className="block"
		>
			<Card className="hover:bg-accent/50 transition-colors cursor-pointer">
				<CardContent className="flex items-center justify-between py-4">
					<div className="flex items-center gap-3">
						<User className="w-5 h-5 text-muted-foreground shrink-0" />
						<div>
							<div className="flex items-center gap-2 flex-wrap">
								<p className="font-medium">
									{patient.firstName} {patient.lastName}
								</p>
								{patient.clientNumber != null && (
									<span className="text-xs font-mono text-muted-foreground bg-muted rounded px-1.5 py-0.5">
										#{patient.clientNumber}
									</span>
								)}
							</div>
							<div className="flex items-center gap-3 mt-0.5 flex-wrap">
								{patient.dateOfBirth && (
									<p className="text-sm text-muted-foreground">
										DOB:{" "}
										{new Date(patient.dateOfBirth).toLocaleDateString("en-CA", {
											year: "numeric",
											month: "short",
											day: "numeric",
										})}
									</p>
								)}
								{patient.kNumber && (
									<p className="text-sm text-muted-foreground">{patient.kNumber}</p>
								)}
							</div>
						</div>
					</div>
					<div className="flex items-center gap-3">
						{patient.clientStatus && (
							<StatusPill status={patient.clientStatus as ClientStatus} />
						)}
						<ChevronRight className="w-4 h-4 text-muted-foreground" />
					</div>
				</CardContent>
			</Card>
		</Link>
	)
}

/* ─── Status pill ────────────────────────────────────────────────────────── */

function StatusPill({ status }: { status: ClientStatus }) {
	return (
		<span
			className={cn(
				"inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium",
				STATUS_COLORS[status] ?? "bg-muted text-muted-foreground",
			)}
		>
			{STATUS_LABELS[status] ?? status}
		</span>
	)
}
